import type { JSX } from "solid-js";
import type { TranslationKey } from "./translations";

/**
 * Helper function to insert JSX elements into translated strings.
 *
 * This function takes a translation key, replaces placeholders with JSX elements,
 * and returns a JSX fragment with the elements properly inserted.
 * It also supports string/number parameters that are passed directly to the translation function.
 *
 * @param t - The translation function
 * @param key - The translation key
 * @param replacements - Object mapping placeholder names to JSX elements or string/number values
 * @returns JSX fragment with translated text and inserted elements
 *
 * @example
 * ```tsx
 * const text = translateWithJSX(t, "invite_modal_create_new_project", {
 *   newProject: <button onClick={...}>new project</button>
 * });
 * ```
 *
 * @example
 * ```tsx
 * const text = translateWithJSX(t, "editor_added_to_knowledge", {
 *   filename: "document.pdf",
 *   clickHere: <button>Click here</button>
 * });
 * ```
 */
export function translateWithJSX(
  t: (key: TranslationKey, params?: Record<string, string | number>) => string,
  key: TranslationKey,
  replacements: Record<string, JSX.Element | string | number>
): JSX.Element {
  // Separate JSX elements from string/number values
  const jsxReplacements: Record<string, JSX.Element> = {};
  const stringParams: Record<string, string | number> = {};

  for (const [placeholder, value] of Object.entries(replacements)) {
    if (typeof value === "string" || typeof value === "number") {
      stringParams[placeholder] = value;
    } else {
      jsxReplacements[placeholder] = value;
    }
  }

  // Get the translated text with placeholder values
  // For JSX elements, use temporary markers; for strings/numbers, use actual values
  // Build params object for t() function - it accepts string | number
  const translationParams: Record<string, string | number> = { ...stringParams };
  // Add JSX placeholder markers as strings
  for (const [placeholder] of Object.entries(jsxReplacements)) {
    translationParams[placeholder] = `__JSX_PLACEHOLDER_${placeholder}__`;
  }

  const text = t(key, translationParams);

  // Build placeholderValues map for later string replacement (all strings)
  const placeholderValues: Record<string, string> = {};
  for (const [placeholder, value] of Object.entries(translationParams)) {
    placeholderValues[placeholder] = String(value);
  }

  // Split by placeholders and insert JSX elements
  const parts: (string | JSX.Element)[] = [];
  let remaining = text;

  for (const [placeholder, element] of Object.entries(jsxReplacements)) {
    const marker = placeholderValues[placeholder];
    const index = remaining.indexOf(marker);

    if (index !== -1) {
      // Add text before the marker
      if (index > 0) {
        parts.push(remaining.slice(0, index));
      }
      // Add the JSX element
      parts.push(element);
      // Continue with the rest
      remaining = remaining.slice(index + marker.length);
    }
  }

  // Add any remaining text
  if (remaining) {
    parts.push(remaining);
  }

  return <>{parts}</>;
}
